<template>
  <div class="animated fadeIn">
    <b-card>
      <div slot="header">
        <b-row>
          <b-col cols="6">
            <span v-if="isUpdateForm">{{ $t('detection.update_title') }}</span>
            <span v-else>{{ $t('detection.create_title') }}</span>
          </b-col>
        </b-row>
      </div>
      <div v-if="spinners.contentIsLoading" class="text-center">
        <b-spinner type="grow" variant="primary"/>
      </div>
      <div v-else>
        <b-form-group>
          <label>{{ $dt('detection.uuid', 'UUID') }}</label>
          <b-form-input type="text" placeholder="uuid"
            v-model="entity.uuid" readonly/>
        </b-form-group>
      </div>
      <template v-if="isUpdateForm">
        <div class="files-grid" v-if="entity?.files?.length > 0">
          <div v-for="file in entity.files" :key="file.id">
            <FilePreview :file="file" :set_id="entity.detection_set_id" image_type="detection"/>
            </div>
        </div>
        <DetectionJson v-if="entity.json_data?.data" :json-data="entity.json_data.data"
          :parsed-data="entity.parsed_data"
          :json-fields="jsonFields"
          :json-field-options="jsonFieldOptions"
          :errors="jsonErrors"
          @update="val => entity.json_data.data = val"
          />
      </template>

      <div slot="footer">
        <b-button type="submit" variant="primary" :disabled="spinners.isSaving" @click="save()">
          <b-spinner v-if="spinners.isSaving" type="grow" small/>
          {{ $t('global.save') }}
        </b-button>
        <router-view :to="{ name: 'Detections' }">
          <b-button type="reset" variant="info" class="ml-2">
            {{ $t('global.cancel') }}
          </b-button>
        </router-view>
      </div>
    </b-card>
  </div>
</template>

<script>
import merge from 'lodash.merge';
import { mapState } from 'vuex';
import Services from '@/services/index';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import FilePreview from '@/views/Shared/FilePreview.vue';
import DetectionJson from '@/views/Detections/DetectionJson.vue';

export default {
  name: 'DetectionForm',
  components: {
    FilePreview,
    DetectionJson
  },
  data() {
    return {
      spinners: {
        contentIsLoading: false,
        isSaving: false,
      },
      entity: this.value,
      errorMsg: this.errors,
      jsonFields: [],
      jsonFieldOptions: {},
      jsonErrors: {},
    };
  },
  props: {
    // If id === null, form will be considered create form else update
    id: { type: Number, default: null },
    // if title == false to hide header, pass string to override default title
    title: { type: [String, Boolean], default: undefined },
    // pass errors from parent form if embedded
    errors: { type: Object, default() { return {}; } },
    // value is used both as DEFAULTs placeholder and for v-model
    value: {
      type: Object,
      default() {
        return {
          name: '',
          tags: [],
        };
      },
    },
    detectionSetId: { type: Number, required: true },
  },
  computed: {
    // embedded form will have value passed using v-model, we could also make a custom prop for checking this
    isEmbedded() { return this.$options.propsData.value !== undefined; },
    isUpdateForm() { return this.id !== null; },
    ...mapState('Auth', ['activeProjectId']),
    projectId() { return this.project_id || this.activeProjectId; },
  },
  mounted() {
    this.load();
  },
  methods: {
    getForUpdate: Services.detections.editData,
    getForCreate: Services.detections.createData,
    pushUpdate: Services.detections.update,
    pushCreate: Services.detections.create,
    load() {
      this.spinners.contentIsLoading = true;
      const action = this.isUpdateForm ? this.getForUpdate : this.getForCreate;
      action(this.activeProjectId, this.detectionSetId, this.$route.params.id).then((data) => {
        this.spinners.contentIsLoading = false;

        this.entity = this.isUpdateForm ? data.data.detection : { ...this.entity, ...data.data.detection };
        this.jsonFields = data.data.json_fields;
        this.jsonFieldOptions = data.data.json_field_options;
      }).catch((data) => {
        this.spinners.contentIsLoading = false;
        this.errorMsg = data.data ? data.data.errors : {};
      });
    },
    save() {
      if (this.isEmbedded) return;
      this.spinners.isSaving = true;

      const action = this.isUpdateForm ? this.pushUpdate : this.pushCreate;
      const submitData = merge({}, this.entity, {
        project_id: this.projectId,
        detection_set_id: this.detectionSetId
      });
      if (submitData.json_data?.data) {
        submitData.json_data.data = JSON.stringify(submitData.json_data.data);
      }

      action(this.activeProjectId, this.detectionSetId, submitData).then((data) => {
        this.spinners.isSaving = false;
        this.jsonErrors = {};
        this.alertSuccess();
        // this.entity = data.data; // This wont work if entity has nested objects/forms
        merge(this.entity, data.data); // This will not delete keys but will preserve original object
        this.errorMsg = {};
        if (!this.isUpdateForm) {
          // After successful create
          // if (this.$can('edit', 'samples')) {
          this.$router.push({ name: 'DetectionUpdate', params: { id: data.data.id } });
          // } else { // do not redirect if cannot edit
          //   this.entity = this.$options.props.value.default();
          //   this.load();
          // }
        }
      }).catch(({ errors, message }) => {
        this.spinners.isSaving = false;
        this.errorMsg = errors;
        this.jsonErrors = errors.json_data;
        this.alertError({ text: message });

        this.$nextTick(() => {
          const firstError = this.$el.querySelector('.alert.alert-danger');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      });
    },
  }
};
</script>

<style scoped lang="scss">
.files-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  grid-gap: 1rem;
  margin-bottom: 1rem;
}
</style>
